<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="5"
                type="text"
                :placeholder="$t('PLACEHOLDERS.sender_name')"
                v-model="filterOptions.user_name"
              />

              <base-input
                col="5"
                type="text"
                :placeholder="$t('PLACEHOLDERS.orderNum')"
                v-model="filterOptions.serial_number"
              />

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="5"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_from')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="5"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_to')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("MESSAGES.Store financial reports (finished orders)") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcelAllData"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:[`item.advertiser_id`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <template v-slot:[`item.serial`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.advertiser_id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <template v-slot:[`item.serial_number`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger">
              <router-link :to="{ path: '/orders/show/' + item.id }">
                {{ item.serial_number }}
              </router-link>
            </h6>
          </div>
        </template>
        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Generate PDF Template Content -->
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="$t('MESSAGES.Store financial reports (finished orders)')"
      :pdf-quality="2"
      pdf-format="a4"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      @hasGenerated="$message.success($t('MESSAGES.generatedSuccessfully'))"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">
            {{ $t("MESSAGES.Store financial reports (finished orders)") }}
          </h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th
                    v-for="(header, index) in tableHeaders"
                    :key="header.value"
                  >
                    <span>{{ header.text }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in tableRows" :key="row.id">
                  <td>
                    {{
                      (paginations.current_page - 1) *
                        paginations.items_per_page +
                      index +
                      1
                    }}
                  </td>
                  <td>{{ row.serial_number }}</td>
                  <td>{{ row.created_at }}</td>
                  <td>{{ row.user_name }}</td>
                  <td>{{ row.tax_amount }}</td>
                  <td>{{ row.total }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <!-- ==== Start:: Overall_statistics Addresses ==== -->
          <div class="table_content mt-5">
            <v-simple-table class="stat-table">
              <template v-slot:default>
                <thead class="all-stat">
                  <tr>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_completed_orders") }}
                    </th>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_tax") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="text-center">
                    <td>{{ Number(totals.total_paid_price).toFixed(3) }}</td>
                    <td>{{ Number(totals.total_paid_tax).toFixed(3) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <!-- ==== End:: Overall_statistics Addresses ==== -->
        </div>
      </section>
    </vue-html2pdf>
    <!-- End:: Generate PDF Template Content -->
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";

export default {
  name: "AllStoreFinancialReports",

  components: {
    VueHtml2pdf,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page || 1;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  data() {
    return {
      loading: false,
      isWaitingRequest: false,
      excelDownloadBtnIsLoading: false,

      filterFormIsActive: false,
      filterOptions: {
        user_name: null,
        serial_number: null,
        from_date: null,
        to_date: null,
      },
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNum"),
          value: "serial_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Orders.orderDate"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.sender_name"),
          value: "user_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.tax_value"),
          value: "tax_amount",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_order_value"),
          value: "total",
          align: "center",
          sortable: false,
        },
      ],
      tableRows: [],
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      totals: {
        total_finished_auctions: 0,
        total_paid_price: 0,
        total_paid_tax: 0,
        total_app_profit: 0,
        total_tax_value: 0,
      },
    };
  },

  methods: {
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async submitFilterForm() {
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.user_name = null;
      this.filterOptions.serial_number = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: this.$route.path,
          query: { ...this.$route.query, page: 1 },
        });
      }
      this.setTableRows();
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "financial-reports/store-orders?status=finished",
          params: {
            page: this.paginations.current_page,
            user_name: this.filterOptions.user_name,
            serial_number: this.filterOptions.serial_number,
            from_date: this.filterOptions.from_date,
            to_date: this.filterOptions.to_date,
          },
        });
        this.loading = false;
        const orders = res.data.data.orders;
        this.tableRows = Array.isArray(orders) ? orders : orders?.data || [];

        this.totals.total_paid_price =
          res.data.data.totals.total_completed_orders;
        this.totals.total_paid_tax = res.data.data.totals.total_vat;

        this.paginations.last_page =
          orders?.meta?.last_page || res.data.meta?.last_page || 1;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
    },
    async downloadExcelAllData() {
      this.loading = true;
      try {
        let response = await this.$axios({
          method: "GET",
          url: "financial-reports/store-export?status=finished",
          params: {
            page: this.paginations.current_page,
            user_name: this.filterOptions.user_name,
            serial_number: this.filterOptions.serial_number,
            from_date: this.filterOptions.from_date,
            to_date: this.filterOptions.to_date,
          },
          responseType: "blob",
        });
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });

        // Create a download link
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        // You can customize the file name from response headers or just use a default
        const filename = response.headers["content-disposition"]
          ? response.headers["content-disposition"]
              .split("filename=")[1]
              .replace(/"/g, "")
          : "report.xlsx";

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$message.error(error.response.data.message);
        console.log(error.response?.data?.message || error.message);
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.paginations.current_page = +this.$route.query.page || 1;
    this.setTableRows();
  },
};
</script>

<style scoped>
.ex-btn-s {
  color: black !important;
  font-size: 20px;
  font-weight: bold;
  text-decoration: underline !important;
}

.all-stat {
  background: var(--main_theme_clr);
}

.all-stat th {
  color: white !important;
  font-size: 16px !important;
  font-weight: 800;
}

.excel {
  background: darkgreen;
  padding: 7px 10px;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
}
</style>
